import { l } from '@manychat/manyui'

import { alert } from 'common/core'
import { IAsyncThunkActionWithReturnValue, IThunkAction } from 'common/core/interfaces/actions'
import { CreateFolderResponse } from 'common/core/interfaces/folder'
import { FIELD_ROOT_FOLDER_ID } from 'common/fields/constants'
import { userFieldFoldersSlice } from 'common/fields/reducers/userFieldFolders'
import { getIsFolderNameExists } from 'common/fields/selectors/userFieldFoldersSelectors'
import { handleError } from 'shared/api/lib/errors/handlers'
import { FoldersApi } from 'shared/api/requests/folders'
import { getClientId } from 'utils/clientId'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import { fetchUserFields, moveToFolderBulkUserField } from './userFieldsActions'

const FOLDER_ENTITY_CUFS = 20
const ROOT_PATH = '/'

export const fetchUserFieldFolders = () =>
  userFieldFoldersSlice.actions.getListFx({
    payload: { body: { entity: FOLDER_ENTITY_CUFS, path: ROOT_PATH } },
  })

export const createUserFieldFolder =
  (title: string): IThunkAction<Promise<CreateFolderResponse | undefined>> =>
  async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return
    }

    try {
      const { data } = await FoldersApi.create({
        body: { title, entity: FOLDER_ENTITY_CUFS, path: ROOT_PATH },
        query: { client_id: getClientId() },
      })
      dispatch(userFieldFoldersSlice.actions.appendItem({ item: data.folder }))
      alert(l.translate('Folder created'), 'success')
      return data
    } catch (error) {
      handleError(error)
      return
    }
  }

export const createFolderAndMoveFields = (title: string, fieldIds: number[]): IThunkAction => {
  return async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return
    }

    const result = await dispatch(createUserFieldFolder(title))
    if (!result) return
    await dispatch(
      moveToFolderBulkUserField({
        fieldIds,
        folderId: result.folder.folder_id,
        oldFolderId: FIELD_ROOT_FOLDER_ID,
      }),
    )
  }
}

export const removeUserFieldFolder =
  (
    folderId: number,
    options: {
      path: string
      delete_content: boolean
    },
  ): IThunkAction =>
  async (dispatch) => {
    try {
      await FoldersApi.delete({ body: options, query: { client_id: getClientId() } })
      dispatch(userFieldFoldersSlice.actions.deleteItem({ id: folderId }))
      dispatch(fetchUserFields({ activeOnly: false, path: '/' }))
      alert(l.translate('Folder deleted!'), 'success')
    } catch (error) {
      handleError(error)
    }
  }

export const updateUserFieldFolderName =
  (folderId: number, path: string, title: string): IAsyncThunkActionWithReturnValue<boolean> =>
  async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return false
    }

    try {
      await FoldersApi.rename({ body: { path, title }, query: { client_id: getClientId() } })
      dispatch(userFieldFoldersSlice.actions.updateItem({ id: folderId, data: { title } }))
      alert(l.translate('Folder renamed!'), 'success')
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

anotherTabNotificationsListener.on('folder_created', (data, dispatch) => {
  if (data.folder.entity === FOLDER_ENTITY_CUFS) {
    dispatch(userFieldFoldersSlice.actions.appendItem({ item: data.folder }))
  }
})

anotherTabNotificationsListener.on('folder_renamed', (data, dispatch) => {
  if (data.folder.entity === FOLDER_ENTITY_CUFS) {
    dispatch(
      userFieldFoldersSlice.actions.updateItem({
        id: data.folder.folder_id,
        data: data.folder,
      }),
    )
  }
})

anotherTabNotificationsListener.on('folder_deleted', (data, dispatch) => {
  if (data.folder) {
    if (data.folder.entity === FOLDER_ENTITY_CUFS) {
      dispatch(userFieldFoldersSlice.actions.deleteItem({ id: data.folder.folder_id }))
    }
  }
})
